import type { DraftStyle } from "../components/boardTypes";
import type { StarterConfig } from "../../shared/components/TeamsBoard";
import type { Position, Player } from "../../shared/models/Player";
import { normalizeName } from "../../shared/utils/espn";
import { formatTeamAbbreviation } from "../../shared/utils/teamAbbreviation";
import { findBestFuzzyNormKey, jaroWinkler } from "../../shared/utils/nameMatch";

type DraftSyncProvider = "sleeper";

type ExternalDraftPick = {
  pickNumber: number;
  round?: number;
  rosterIndex?: number;
  playerName: string;
  playerId?: string;
  team?: string;
  position?: string;
};

export type DraftSyncPayload = {
  provider: DraftSyncProvider;
  sourceLabel: string;
  teams?: number;
  rounds?: number;
  draftStyle?: DraftStyle;
  teamNames?: string[];
  rosterStarterConfig?: StarterConfig;
  draftSlotTeamIndexes?: Array<number | null>;
  picks: ExternalDraftPick[];
};

type DraftSyncApplied = {
  teams: number;
  rounds: number;
  draftStyle?: DraftStyle;
  teamNames?: string[];
  rosterStarterConfig?: StarterConfig;
  draftSlots: (string | null)[];
  draftSlotTeamIndexes: Array<number | null>;
  draftedIds: Set<string>;
  draftedOrder: string[];
  unmatchedPicks: ExternalDraftPick[];
  createdPlayers: Player[];
};

type PlayerIndex = {
  byId: Map<string, Player>;
  byExactNormName: Map<string, Player[]>;
  byLastToken: Map<string, Player[]>;
  normKeys: string[];
};

const FIRST_NAME_ALIASES: Record<string, string[]> = {
  chig: ["chigoziem"],
  chigoziem: ["chig"],
  hollywood: ["marquise"],
  marquise: ["hollywood"],
  gabe: ["gabriel"],
  gabriel: ["gabe"],
  mike: ["michael"],
  michael: ["mike"],
  chris: ["christopher"],
  christopher: ["chris"],
  pat: ["patrick"],
  patrick: ["pat"],
};

function tokenizeNormName(value: string) {
  return normalizeName(value).split(" ").filter(Boolean);
}

function getLastToken(value: string) {
  const tokens = tokenizeNormName(value);
  return tokens[tokens.length - 1] ?? "";
}

function normalizeTeam(team: string | undefined) {
  return formatTeamAbbreviation(team).replace(/[^A-Z]/g, "").trim();
}

function normalizePosition(position: string | undefined): Position | "" {
  const normalized = String(position ?? "").toUpperCase().trim();
  if (normalized === "D/ST" || normalized === "DEF") return "DST";
  if (normalized === "PK") return "K";
  if (
    normalized === "QB" ||
    normalized === "RB" ||
    normalized === "WR" ||
    normalized === "TE" ||
    normalized === "K" ||
    normalized === "DST"
  ) {
    return normalized;
  }
  return "";
}

function firstNamesAlign(playerFirst: string, pickFirst: string) {
  if (!playerFirst || !pickFirst) return false;
  return (
    playerFirst === pickFirst ||
    playerFirst[0] === pickFirst[0] ||
    FIRST_NAME_ALIASES[playerFirst]?.includes(pickFirst) ||
    FIRST_NAME_ALIASES[pickFirst]?.includes(playerFirst)
  );
}

function createPlayerIndex(playersById: Record<string, Player>): PlayerIndex {
  const byId = new Map<string, Player>();
  const byExactNormName = new Map<string, Player[]>();
  const byLastToken = new Map<string, Player[]>();

  Object.values(playersById).forEach((player) => {
    byId.set(String(player.id), player);
    const norm = normalizeName(player.name);
    if (!norm) return;
    const list = byExactNormName.get(norm) ?? [];
    list.push(player);
    byExactNormName.set(norm, list);

    const lastToken = getLastToken(norm);
    if (lastToken) {
      const sameLastName = byLastToken.get(lastToken) ?? [];
      sameLastName.push(player);
      byLastToken.set(lastToken, sameLastName);
    }
  });

  return {
    byId,
    byExactNormName,
    byLastToken,
    normKeys: Array.from(byExactNormName.keys()),
  };
}

function getNameSimilarity(player: Player, pick: ExternalDraftPick) {
  const playerNorm = normalizeName(player.name);
  const pickNorm = normalizeName(pick.playerName);
  if (!playerNorm || !pickNorm) return 0;
  if (playerNorm === pickNorm) return 1;

  const playerTokens = tokenizeNormName(player.name);
  const pickTokens = tokenizeNormName(pick.playerName);
  const playerLast = playerTokens[playerTokens.length - 1] ?? "";
  const pickLast = pickTokens[pickTokens.length - 1] ?? "";
  const playerFirst = playerTokens[0] ?? "";
  const pickFirst = pickTokens[0] ?? "";
  const baseScore = jaroWinkler(playerNorm, pickNorm);

  if (playerLast && pickLast && playerLast === pickLast) {
    if (firstNamesAlign(playerFirst, pickFirst)) return Math.max(baseScore, 0.84);
  }

  return baseScore;
}

function scoreCandidate(player: Player, pick: ExternalDraftPick) {
  const nameScore = getNameSimilarity(player, pick);
  let score = 0;

  const playerTeam = normalizeTeam(player.team);
  const pickTeam = normalizeTeam(pick.team);
  const teamMatches = Boolean(playerTeam && pickTeam && playerTeam === pickTeam);
  if (playerTeam && pickTeam) {
    score += teamMatches ? 3 : -1;
  }

  const playerPos = normalizePosition(player.position);
  const pickPos = normalizePosition(pick.position);
  const positionMatches = Boolean(playerPos && pickPos && playerPos === pickPos);
  if (playerPos && pickPos) {
    score += positionMatches ? 2 : -2;
  }

  return score + nameScore * 10;
}

function isAcceptableCloseMatch(player: Player, pick: ExternalDraftPick) {
  const nameScore = getNameSimilarity(player, pick);
  if (nameScore >= 0.88) return true;

  const playerTokens = tokenizeNormName(player.name);
  const pickTokens = tokenizeNormName(pick.playerName);
  const playerLast = playerTokens[playerTokens.length - 1] ?? "";
  const pickLast = pickTokens[pickTokens.length - 1] ?? "";
  if (!playerLast || !pickLast || playerLast !== pickLast) return false;

  const playerTeam = normalizeTeam(player.team);
  const pickTeam = normalizeTeam(pick.team);
  const playerPos = normalizePosition(player.position);
  const pickPos = normalizePosition(pick.position);
  const teamMatches = Boolean(playerTeam && pickTeam && playerTeam === pickTeam);
  const positionMatches = Boolean(playerPos && pickPos && playerPos === pickPos);

  if (teamMatches && positionMatches && nameScore >= 0.55) return true;
  if (positionMatches && nameScore >= 0.78) return true;
  return false;
}

function chooseBestCandidate(candidates: Player[], pick: ExternalDraftPick) {
  let best: Player | undefined;
  let bestScore = Number.NEGATIVE_INFINITY;

  candidates.forEach((candidate) => {
    const score = scoreCandidate(candidate, pick);
    if (!best || score > bestScore) {
      best = candidate;
      bestScore = score;
    }
  });

  return best;
}

function matchPickToPlayer(pick: ExternalDraftPick, index: PlayerIndex) {
  const rawPlayerId = String(pick.playerId ?? "").trim();
  if (rawPlayerId) {
    const direct = index.byId.get(rawPlayerId);
    if (direct) return direct;
  }

  const normName = normalizeName(pick.playerName);
  if (!normName) return undefined;

  const exact = index.byExactNormName.get(normName);
  if (exact?.length) return chooseBestCandidate(exact, pick);

  const targetTokens = tokenizeNormName(normName);
  const targetLastName = targetTokens[targetTokens.length - 1] ?? "";
  const sameLastNameCandidates = targetLastName ? index.byLastToken.get(targetLastName) ?? [] : [];
  const closeCandidates = sameLastNameCandidates.filter((candidate) => isAcceptableCloseMatch(candidate, pick));
  if (closeCandidates.length) return chooseBestCandidate(closeCandidates, pick);

  const fuzzy = findBestFuzzyNormKey({
    targetNorm: normName,
    candidateNormKeys: index.normKeys,
    minScore: 0.88,
  });
  if (fuzzy) {
    const fuzzyCandidates = index.byExactNormName.get(fuzzy.key)?.filter((candidate) => isAcceptableCloseMatch(candidate, pick));
    if (fuzzyCandidates?.length) return chooseBestCandidate(fuzzyCandidates, pick);
  }

  return undefined;
}

function inferRounds(picksLength: number, teams: number, requestedRounds?: number) {
  const minimum = Math.max(1, Math.ceil(Math.max(1, picksLength) / Math.max(1, teams)));
  return Math.max(minimum, requestedRounds ?? minimum);
}

function isReverseRound(roundIndex: number, style: DraftStyle) {
  if (style === "Regular Draft") return false;
  if (style === "Snake Draft") return roundIndex % 2 === 1;
  return roundIndex === 1 || (roundIndex >= 2 && roundIndex % 2 === 0);
}

function getOriginalTeamIndexForPick(pickIndex: number, teams: number, draftStyle: DraftStyle): number {
  const roundIndex = Math.floor(pickIndex / teams);
  const pickOffset = pickIndex % teams;
  return isReverseRound(roundIndex, draftStyle) ? teams - 1 - pickOffset : pickOffset;
}

function normalizeDraftSlotTeamIndexes(args: {
  source?: Array<number | null>;
  totalSlots: number;
  teams: number;
  draftStyle: DraftStyle;
}) {
  const { source, totalSlots, teams, draftStyle } = args;

  return Array.from({ length: totalSlots }, (_, pickIndex) => {
    const value = source?.[pickIndex];
    if (typeof value === "number" && Number.isFinite(value) && value >= 0 && value < teams) {
      return Math.round(value);
    }
    return getOriginalTeamIndexForPick(pickIndex, teams, draftStyle);
  });
}

function slugify(value: string) {
  return value
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .trim();
}

function createPlayerFromPick(pick: ExternalDraftPick, provider: DraftSyncProvider): Player | undefined {
  const name = String(pick.playerName ?? "").trim();
  const position = normalizePosition(pick.position);
  if (!name || !position) return undefined;

  const rawPlayerId = String(pick.playerId ?? "").trim();
  const id = rawPlayerId || `synced-${provider}-${slugify(`${name}-${pick.team ?? ""}-${position}`)}`;

  return {
    id,
    name,
    position,
    team: String(pick.team ?? "").trim() || undefined,
  };
}

export function applyDraftSyncPayload(args: {
  payload: DraftSyncPayload;
  playersById: Record<string, Player>;
  fallbackTeams: number;
  fallbackRounds: number;
  fallbackTeamNames: string[];
}) {
  const { payload, playersById, fallbackTeams, fallbackRounds, fallbackTeamNames } = args;
  const index = createPlayerIndex(playersById);
  const sortedPicks = [...payload.picks]
    .filter((pick) => Number.isFinite(pick.pickNumber) && pick.pickNumber > 0 && pick.playerName.trim())
    .sort((left, right) => left.pickNumber - right.pickNumber);

  const teams = Math.max(2, Math.min(20, Math.round(payload.teams ?? fallbackTeams)));
  const rounds = Math.max(
    1,
    Math.min(40, inferRounds(Math.max(sortedPicks.length, payload.draftSlotTeamIndexes?.length ?? 0), teams, payload.rounds ?? fallbackRounds))
  );
  const totalSlots = teams * rounds;
  const draftStyle = payload.draftStyle ?? "Snake Draft";
  const draftSlots = Array.from({ length: totalSlots }, () => null as string | null);
  const draftSlotTeamIndexes = normalizeDraftSlotTeamIndexes({
    source: payload.draftSlotTeamIndexes,
    totalSlots,
    teams,
    draftStyle,
  });
  const draftedIds = new Set<string>();
  const draftedOrder: string[] = [];
  const unmatchedPicks: ExternalDraftPick[] = [];
  const createdPlayers: Player[] = [];
  const createdPlayersById = new Map<string, Player>();

  sortedPicks.forEach((pick) => {
    const slotIndex = pick.pickNumber - 1;
    if (slotIndex < 0 || slotIndex >= totalSlots) return;

    const matchedPlayer = matchPickToPlayer(pick, index);
    const player = matchedPlayer ?? createPlayerFromPick(pick, payload.provider);

    if (player && !matchedPlayer && !createdPlayersById.has(player.id)) {
      createdPlayersById.set(player.id, player);
      createdPlayers.push(player);
    }

    if (!player || draftedIds.has(player.id)) {
      unmatchedPicks.push(pick);
      return;
    }

    draftSlots[slotIndex] = player.id;
    draftedIds.add(player.id);
    draftedOrder.push(player.id);
  });

  const teamNames = payload.teamNames?.length
    ? Array.from({ length: teams }, (_, index) => payload.teamNames?.[index] ?? `Team ${index + 1}`)
    : Array.from({ length: teams }, (_, index) => fallbackTeamNames[index] ?? `Team ${index + 1}`);

  return {
    teams,
    rounds,
    draftStyle: payload.draftStyle,
    teamNames,
    rosterStarterConfig: payload.rosterStarterConfig,
    draftSlots,
    draftSlotTeamIndexes,
    draftedIds,
    draftedOrder,
    unmatchedPicks,
    createdPlayers,
  } satisfies DraftSyncApplied;
}
